import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShieldAlert, X } from 'lucide-react';
import styles from './PopupNotification.module.scss';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const STORAGE_KEY = 'ntp:safety-popup-dismissed';
const SAFETY_POPUP_EXPIRES_AT = '2026-09-10T23:59:59+05:45';

const FALLBACK_POPUP = {
  id: 'flood-safety-rasuwa-2026-08-27',
  active: true,
  title: 'Travel safety update',
  content: 'Our thoughts are with everyone affected by the Rasuwa flash flood. We are closely monitoring local travel conditions.\n\nIf you are travelling through an affected area, follow local guidance and confirm your route before leaving. Please stay safe.',
  imageUrl: '/images/flood-safety-rasuwa.png',
  linkUrl: '',
  expiresAt: SAFETY_POPUP_EXPIRES_AT,
  updatedAt: '2026-08-27',
};

const isHomeRoute = (pathname) => pathname === '/';

const isExpired = (popup) => popup?.expiresAt && Date.parse(popup.expiresAt) <= Date.now();

const contentParagraphs = (content) => String(content || FALLBACK_POPUP.content)
  .split(/\n\s*\n/)
  .filter(Boolean);

const getPopupFingerprint = (popup) => [
  popup?.id || 'fallback',
  popup?.updatedAt || popup?.title || 'safety-notice',
  popup?.expiresAt || SAFETY_POPUP_EXPIRES_AT,
].join(':');

const readDismissedFingerprint = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const saveDismissedFingerprint = (fingerprint) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, fingerprint);
  } catch {
    // Storage can be unavailable in private browsing or hardened webviews.
  }
};

const PopupNotification = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const closeButtonRef = useRef(null);
  const [popup, setPopup] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  const dismissPopup = useCallback(() => {
    if (popup) saveDismissedFingerprint(getPopupFingerprint(popup));
    setIsVisible(false);
  }, [popup]);

  useEffect(() => {
    if (!isHomeRoute(location.pathname)) {
      setIsVisible(false);
      return undefined;
    }

    let cancelled = false;

    const loadPopup = async () => {
      let nextPopup = FALLBACK_POPUP;

      try {
        const response = await axios.get(`${API_BASE_URL}/popups/active`, {
          validateStatus: (status) => status === 200 || status === 204,
        });

        if (response.status === 200 && response.data?.active) {
          nextPopup = {
            ...FALLBACK_POPUP,
            ...response.data,
            imageUrl: response.data.imageUrl || FALLBACK_POPUP.imageUrl,
            expiresAt: response.data.expiresAt || FALLBACK_POPUP.expiresAt,
          };
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('Safety popup API unavailable; using local fallback.', error);
        }
      }

      if (cancelled || isExpired(nextPopup)) return;

      setPopup(nextPopup);
      setIsVisible(readDismissedFingerprint() !== getPopupFingerprint(nextPopup));
    };

    loadPopup();

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!isVisible) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousTouchAction = document.body.style.touchAction;
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') dismissPopup();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.touchAction = previousTouchAction;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [dismissPopup, isVisible]);

  const handleLink = () => {
    if (!popup?.linkUrl) {
      dismissPopup();
      return;
    }

    dismissPopup();
    if (popup.linkUrl.startsWith('/')) {
      navigate(popup.linkUrl);
    } else {
      window.open(popup.linkUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (!popup || !isVisible || !isHomeRoute(location.pathname)) return null;

  const paragraphs = contentParagraphs(popup.content);

  return (
    <div
      className={styles.overlay}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) dismissPopup();
      }}
    >
      <section
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="safety-popup-title"
        aria-describedby="safety-popup-content"
      >
        <div className={styles.mediaFrame}>
          <img
            className={styles.media}
            src={popup.imageUrl}
            alt="Flood aftermath in a misty Nepal mountain valley"
          />
          <div className={styles.mediaShade} aria-hidden="true" />
        </div>

        <div className={styles.content}>
          <button
            ref={closeButtonRef}
            type="button"
            className={styles.closeButton}
            aria-label="Close safety notice"
            onClick={dismissPopup}
          >
            <X size={20} strokeWidth={2.2} />
          </button>

          <div className={styles.eyebrow}>
            <ShieldAlert size={17} aria-hidden="true" />
            <span>Travel advisory</span>
          </div>
          <h2 id="safety-popup-title">{popup.title || FALLBACK_POPUP.title}</h2>
          <div id="safety-popup-content" className={styles.message}>
            {paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
          </div>

          <div className={styles.actions}>
            <button type="button" className={styles.primaryAction} onClick={handleLink}>
              {popup.linkUrl ? 'View update' : 'Understood'}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PopupNotification;
